// src/components/contacts/Contacts.jsx
import React, { useState } from 'react';
import { useAuth } from '../../authContext';

// Custom Hook importieren
import { useContacts } from '../contacts/useContacts';

// Shared Components importieren (falls vorhanden)
import Modal from '../shared/Modal';
import StatusBadge from '../shared/StatusBadge';
import SearchBar from '../shared/SearchBar';
import DataTable from '../shared/DataTable';
import ConfirmModal from '../shared/ConfirmModal';

// Lokale Komponente für das Kontaktformular (kann später extrahiert werden)
import ContactForm from '../contacts/ContactForm';

const Contacts = () => {
  // Authentifizierung und Rolle
  const { role, userId } = useAuth();

  // Custom Hook nutzen
  const {
    contacts,
    companies,
    loading,
    error,
    fetchContactsAndCompanies,
    toggleContactStatus,
    addContact,
    editContact,
    addTask,
    isAllgemeinContact,
  } = useContacts();

  // Modal-State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // State für ausgewählte Contact-IDs
  const [selectedContactForToggle, setSelectedContactForToggle] = useState(null);

  // State für Formular-Daten (Kontakte)
  const [newContact, setNewContact] = useState({
    companyId: '',
    firstName: '',
    lastName: '',
    position: '',
    email: '',
    phone: '',
    remarks: '',
    isDefault: false,
  });
  const [editingContact, setEditingContact] = useState(null); // Umbenannt, um Namenskonflikt zu vermeiden
  const [viewContact, setViewContact] = useState(null);

  // State für neue Aufgabe
  const [newTask, setNewTask] = useState({
    Title: '',
    Description: '',
    ContactId: '',
    DueDate: '',
    Status: 'TODO',
  });

  // Filter-State
  const [searchName, setSearchName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDefaultFilter, setIsDefaultFilter] = useState('all');
  const [searchCompany, setSearchCompany] = useState('');

  // Gefilterte Kontakte
  const filteredContacts = contacts.filter((contact) => {
    const nameMatch =
      contact.firstName.toLowerCase().includes(searchName.toLowerCase()) ||
      contact.lastName.toLowerCase().includes(searchName.toLowerCase());
    const companyMatch =
      searchCompany === '' ||
      (companies.find((c) => c.id === contact.companyId)?.name || '').toLowerCase().includes(searchCompany.toLowerCase());
    const statusMatch =
      statusFilter === 'all' ||
      (statusFilter === 'active' && contact.active) ||
      (statusFilter === 'inactive' && !contact.active);
    const defaultMatch =
      isDefaultFilter === 'all' ||
      (isDefaultFilter === 'yes' && contact.isDefault) ||
      (isDefaultFilter === 'no' && !contact.isDefault);
    return nameMatch && companyMatch && statusMatch && defaultMatch;
  });

  // Funktion zum Öffnen des Status-Toggle-Dialogs (NUR FÜR ADMINS)
  const openToggleStatusModal = (contactId, currentStatus) => {
    if (role !== 'Admin') {
      setErrorModalMessage('Nur Admins können den Status ändern.');
      setShowErrorModal(true);
      return;
    }

    // Prüfe, ob der Kontakt ein Standardkontakt ist und auf "Inaktiv" gesetzt werden soll
    const contact = contacts.find((c) => c.id === contactId);
    if (contact?.isDefault && currentStatus) {
      setErrorModalMessage('Standardkontakte dürfen nicht deaktiviert werden.');
      setShowErrorModal(true);
      return;
    }

    setSelectedContactForToggle({ id: contactId, currentStatus });
    setShowToggleStatusModal(true);
  };

  // Status-Toggle (nutzt den Hook)
  const handleToggleStatus = async (contactId, currentStatus) => {
    try {
      await toggleContactStatus(contactId, currentStatus);
      setShowToggleStatusModal(false);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
      setShowToggleStatusModal(false);
    }
  };

  // Funktion zum Öffnen des Aufgaben-Modals mit voreingestelltem Kontakt
  const handleOpenAddTaskModal = (contact) => {
    setNewTask({
      Title: '',
      Description: '',
      ContactId: contact.id.toString(),
      DueDate: '',
      Status: 'TODO',
    });
    setShowAddTaskModal(true);
  };

  // Funktion zum Speichern der neuen Aufgabe (nutzt den Hook)
  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await addTask(newTask);
      setShowAddTaskModal(false);
      setNewTask({
        Title: '',
        Description: '',
        ContactId: '',
        DueDate: '',
        Status: 'TODO',
      });
    } catch (err) {
      setErrorModalMessage('Fehler beim Erstellen der Aufgabe.');
      setShowErrorModal(true);
      console.error(err);
    }
  };

  // Neuer Kontakt: Speichern (nutzt den Hook)
  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await addContact(newContact);
      setShowAddModal(false);
      setNewContact({
        companyId: '',
        firstName: '',
        lastName: '',
        position: '',
        email: '',
        phone: '',
        remarks: '',
        isDefault: false,
      });
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
    }
  };

  // Kontakt bearbeiten: Speichern (nutzt den Hook)
  const handleEditContact = async (e) => {
    e.preventDefault();
    try {
      await editContact(editingContact.id, editingContact);
      setShowEditModal(false);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
    }
  };

  // Suchfelder für SearchBar (falls Shared Component genutzt wird)
  const searchFields = [
    { type: 'text', placeholder: 'Suche nach Name...', value: searchName, onChange: setSearchName },
    { type: 'text', placeholder: 'Suche nach Unternehmen...', value: searchCompany, onChange: setSearchCompany },
    {
      type: 'select',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'Alle Status' },
        { value: 'active', label: 'Aktiv' },
        { value: 'inactive', label: 'Inaktiv' },
      ],
    },
    {
      type: 'select',
      value: isDefaultFilter,
      onChange: setIsDefaultFilter,
      options: [
        { value: 'all', label: 'Alle Standardkontakte' },
        { value: 'yes', label: 'Standard' },
        { value: 'no', label: 'Nicht Standard' },
      ],
    },
  ];

  // Tabellen-Spalten für DataTable (falls Shared Component genutzt wird)
  const columns = [
    { key: 'name', header: 'Name', render: (contact) => `${contact.firstName} ${contact.lastName}` },
    { key: 'company', header: 'Unternehmen', render: (contact) => companies.find((c) => c.id === contact.companyId)?.name || 'Unbekannt' },
    { key: 'position', header: 'Position', render: (contact) => contact.position || '–' },
    { key: 'email', header: 'E-Mail', render: (contact) => contact.email || '–' },
    { key: 'phone', header: 'Telefon', render: (contact) => contact.phone || '–' },
    {
      key: 'isDefault',
      header: 'Standard',
      render: (contact) => (
        <span className={`default-badge ${contact.isDefault ? 'default' : 'not-default'}`}>
          {contact.isDefault ? 'Ja' : 'Nein'}
        </span>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (contact) => (
        <StatusBadge
          value={contact.active}
          onClick={() => openToggleStatusModal(contact.id, contact.active)}
          type="status"
        />
      ),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      render: (contact) => (
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            className="action-button add-task"
            onClick={() => handleOpenAddTaskModal(contact)}
            title="Neue Aufgabe für diesen Kontakt erstellen"
          >
            📝
          </button>
          <button
            className="action-button view"
            onClick={() => {
              setViewContact(contact);
              setShowViewModal(true);
            }}
          >
            👁️
          </button>
          {role === 'Admin' && (
            <button
              className="action-button edit"
              onClick={() => {
                setEditingContact({ ...contact });
                setShowEditModal(true);
              }}
            >
              ✏️
            </button>
          )}
        </div>
      ),
    },
  ];

  // --- Render ---
  return (
    <div className="contacts-tab">
      {/* Toolbar mit Suchfeldern und "Neuer Ansprechpartner"-Button */}
      <div className="toolbar">
        <SearchBar fields={searchFields} />
        {role === 'Admin' && (
          <button
            className="add-button"
            onClick={() => {
              setNewContact({
                companyId: '',
                firstName: '',
                lastName: '',
                position: '',
                email: '',
                phone: '',
                remarks: '',
                isDefault: false,
              });
              setShowAddModal(true);
            }}
          >
            ➕ Neuer Ansprechpartner
          </button>
        )}
      </div>

      {/* Tabelle oder Lade-/Fehlermeldung */}
      {loading ? (
        <p>Lade Ansprechpartner...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <DataTable columns={columns} data={filteredContacts} keyField="id" />
      )}

      {/* Modal: Neuer Ansprechpartner */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Neuer Ansprechpartner"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowAddModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleAddContact}>
              Speichern
            </button>
          </div>
        }
      >
        <form onSubmit={handleAddContact}>
          <ContactForm data={newContact} onChange={setNewContact} companies={companies} />
        </form>
      </Modal>

      {/* Modal: Ansprechpartner bearbeiten */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Ansprechpartner bearbeiten"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowEditModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleEditContact}>
              Speichern
            </button>
          </div>
        }
      >
        {editingContact && (
          <form onSubmit={handleEditContact}>
            <ContactForm
              data={editingContact}
              onChange={setEditingContact}
              companies={companies}
              isEditMode={true}
            />
          </form>
        )}
      </Modal>

      {/* Modal: Ansprechpartner anzeigen */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={`${viewContact?.firstName || ''} ${viewContact?.lastName || ''}`}
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowViewModal(false)}>
              Schließen
            </button>
          </div>
        }
      >
        {viewContact && (
          <div className="view-modal-content">
            <p><strong>Unternehmen:</strong> {companies.find((c) => c.id === viewContact.companyId)?.name || 'Unbekannt'}</p>
            <p><strong>Position:</strong> {viewContact.position || 'Nicht angegeben'}</p>
            <p><strong>E-Mail:</strong> {viewContact.email || 'Nicht angegeben'}</p>
            <p><strong>Telefon:</strong> {viewContact.phone || 'Nicht angegeben'}</p>
            <p><strong>Bemerkungen:</strong> {viewContact.remarks || 'Keine'}</p>
            <p><strong>Standardkontakt:</strong> {viewContact.isDefault ? 'Ja' : 'Nein'}</p>
            <p><strong>Status:</strong> {viewContact.active ? 'Aktiv' : 'Inaktiv'}</p>
          </div>
        )}
      </Modal>

      {/* Modal: Status ändern */}
      <ConfirmModal
        isOpen={showToggleStatusModal}
        onClose={() => setShowToggleStatusModal(false)}
        onConfirm={() => handleToggleStatus(selectedContactForToggle.id, selectedContactForToggle.currentStatus)}
        title="Status ändern"
        message={
          <>
            Möchten Sie den Status des Ansprechpartners{' '}
            <strong>
              {contacts.find((c) => c.id === selectedContactForToggle?.id)?.firstName}{' '}
              {contacts.find((c) => c.id === selectedContactForToggle?.id)?.lastName}
            </strong>
            wirklich auf <strong>{selectedContactForToggle?.currentStatus ? 'Inaktiv' : 'Aktiv'}</strong> ändern?
          </>
        }
        confirmText="Ja, ändern"
      />

      {/* Modal: Neue Aufgabe */}
      <Modal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        title="Neue Aufgabe"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowAddTaskModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleAddTask}>
              Speichern
            </button>
          </div>
        }
      >
        <form onSubmit={handleAddTask}>
          <div className="form-group">
            <label>Titel:*</label>
            <input
              type="text"
              value={newTask.Title || ''}
              onChange={(e) => setNewTask({ ...newTask, Title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Beschreibung:</label>
            <textarea
              value={newTask.Description || ''}
              onChange={(e) => setNewTask({ ...newTask, Description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Kontakt:*</label>
            <select
              value={newTask.ContactId || ''}
              onChange={(e) => setNewTask({ ...newTask, ContactId: e.target.value })}
              required
              className="filter-select"
              disabled
            >
              <option value="">
                {contacts.find((c) => c.id === parseInt(newTask.ContactId))?.firstName}{' '}
                {contacts.find((c) => c.id === parseInt(newTask.ContactId))?.lastName} (
                {companies.find((c) => c.id === contacts.find((ct) => ct.id === parseInt(newTask.ContactId))?.companyId)?.name ||
                  'Unbekannt'}
                )
              </option>
            </select>
          </div>
          <div className="form-group">
            <label>Fällig am:</label>
            <input
              type="date"
              value={newTask.DueDate || ''}
              onChange={(e) => setNewTask({ ...newTask, DueDate: e.target.value })}
            />
          </div>
        </form>
      </Modal>

      {/* Modal: Fehlermeldung */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Fehler"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowErrorModal(false)}>
              OK
            </button>
          </div>
        }
      >
        <p>{errorModalMessage}</p>
      </Modal>
    </div>
  );
};

export default Contacts;